import { PayloadAction } from '@reduxjs/toolkit';
import { CompanySWOTAnalysis, CompanySWOTRefresh, GetAccountClusterRequest, GetClusterCompanyNewsRequest, GetProductAndServicesRequest, GetProfileRequest, GetStrategicHighlightsRequest, ManageAccountClusterRequest, ShareAccountClusterRequest } from '../Types/RequestBodies';
import { getCurrentTime } from '../Utils/UtilityFunctions';
import { AccountClustersDataResponse, ClusterDetailsResponse, ManageClustersResponse, ShareClusterResponse } from '../Types/ClustersTypes';
import {
  all,
  call, put, select, takeEvery, takeLatest, takeLeading,
} from 'redux-saga/effects';
import axios from 'Utils/Axios';
import { API } from '../Utils/Constants';
import { actions } from '../Slice/ClustersSlice';
import { actions as helperActions } from '../Slice/HelperSlice';
import { CompanyFilingResponse, CompanyNewsResponse, CompanyProfileResponse, CompanyPulseResponse, CompanyStockChartsResponse, CompanySWOTResponse, StrategicHighlightResponse, SWOTRefreshResponse } from '../Types/CompaniesTypes';
import unset from 'lodash/unset';
import { ClustersState } from '../Types/GlobalTypes';
import { ProductAndServiceResponse } from '../Types/ResponseBodies';

export function* getAccountClusters(dispatched: PayloadAction<GetAccountClusterRequest>) {
  const startTime = getCurrentTime();
  const payload = dispatched?.payload;
  const { type, ...requestPayload } = payload;
  try {
    const response: AccountClustersDataResponse = yield call(
      axios.post,
      API.GET_ACCOUNT_CLUSTERS,
      requestPayload,
    )
    if (response?.status === true) {
      yield put(actions.setAllClusters({ results: response?.response, page: payload.page, type: type }));
    } else {
      yield put(actions.accountClustersRequestFailed({ type }));
    }
  } catch (err) {
    console.log(err);
    yield put(actions.accountClustersRequestFailed({ type }));
  }
}

export function* manageAccountCluster(dispatched: PayloadAction<ManageAccountClusterRequest>) {
  try {
    const response: ManageClustersResponse = yield call(
      axios.post,
      API.MANAGE_ACCOUNT_CLUSTERS,
      dispatched?.payload,
    )
    if (response?.status === true) {
      if (dispatched?.payload.type === "create") {
        yield put(actions.createCluster(response));
      }
      if (dispatched?.payload.type === "delete") {
        yield put(actions.deleteCluster(response));
      }
      if (dispatched?.payload.type === "edit") {
        yield put(actions.updateClusterDetails(response));
      }
    } else {
      if (dispatched.payload.type === 'create') {
        yield put(actions.resetClusterManageLoading());
        yield put(helperActions.setNotification({
          message: response?.message,
          proTip: false,
          rightPlacement: false,
          groupId: 0,
          classnameFlag: false
        }))
      }
      if (dispatched.payload.type === "edit") {
        if ((dispatched?.payload?.removedCompanyIds?.length ?? 0) > 0) {
          yield put(actions.setEditErrorState());
          yield put(helperActions.setNotification({
            message: response?.message,
            proTip: false,
            rightPlacement: false,
            groupId: 0,
            classnameFlag: false
          }))
        }
      }
    }

  } catch (err) {
    console.log(err);
    if (dispatched?.payload?.type === 'create' || dispatched?.payload?.type === 'edit') {
      yield put(actions.resetClusterManageLoading());
    }
  }
}

export function* shareAccountClusters(dispatched: PayloadAction<ShareAccountClusterRequest>) {
  try {
    const response: ShareClusterResponse = yield call(
      axios.post,
      API.SHARE_ACCOUNT_CLUSTERS,
      dispatched?.payload
    )
    if (response?.status === true) {
      yield put(actions.setShareStatus(true));
    }
  } catch (err) {
    console.log(err)
  }

}

export function* getClusterDetails(dispatched: PayloadAction<{ clusterId: number }>) {
  const { clusterId } = dispatched.payload;
  const clusters: ClustersState = yield select((state: { clusters: ClustersState }) => state.clusters);

  if (clusters.clusterId === clusterId && clusters.clusterCompanies.length > 0) {
    return;
  }

  try {
    const response: ClusterDetailsResponse = yield call(
      axios.post,
      API.GET_CLUSTER_DETAILS,
      dispatched?.payload
    )
    if (response?.status === true) {
      yield put(actions.setClusterDetails(response.response));
    } else {
      yield put(actions.resetClusterManageLoading());
    }
  } catch (err) {
    console.log(err);
    yield put(actions.resetClusterManageLoading());
  }
}

export function* getClusterStocksChart(dispatched: PayloadAction<GetProfileRequest>) {
  try {
    const response: CompanyStockChartsResponse = yield call(
      axios.post,
      API.COMPANY_STOCK_CHART,
      dispatched?.payload,
    );
    if (response?.status === true) {
      yield put(actions.setClusterStocksChart({ companyId: dispatched?.payload?.companyId, charts: response?.response }));
    }
  } catch (err) {
    console.log(err);
  }

}

export function* getClusterCompanyProfile(dispatched: PayloadAction<GetProfileRequest>) {
  const startTime = getCurrentTime();
  try {
    const { payload } = dispatched;
    const { companyId } = payload;
    const response: CompanyProfileResponse = yield call(
      axios.post,
      API.COMPANY_PROFILE,
      payload,
    );
    if (response?.status === true) {
      const { companyName } = response.response;
      yield put(actions.setClusterCompanyProfile({ companyId: companyId, profile: response?.response }));
    } else {
      yield put(actions.setClusterCompanyProfile({ companyId: companyId, profile: null }));
    }
  } catch (err) {
    console.log(err);
    yield put(actions.setClusterCompanyProfile({
      companyId: dispatched.payload.companyId,
      profile: null,
    }));
  }
}

export function* getClusterStrategicHighlight(dispatched: PayloadAction<{ id: number, type: string, key: string }>) {
  try {
    const response: StrategicHighlightResponse = yield call(
      axios.post,
      API.GET_STRATEGIC_HIGHLIGHTS,
      dispatched?.payload,
    );
    if (response?.status === true) {
      const highlightsArray = response.response?.data || response?.response || [];
      const blip = response.response?.blip || false;
      // yield put(actions.setBlip(blip));
      yield put(actions.setClusterStrategicHighlight({
        id: dispatched?.payload?.id,
        highlights: highlightsArray,
      }))
    }
  } catch (err) {
    console.log(err);
  }
}

export function* getClusterSWOTAnalysis(dispatched: PayloadAction<CompanySWOTAnalysis>) {
  try {
    const response: CompanySWOTResponse = yield call(
      axios.post,
      API.GET_SWOT_ANALYSIS,
      dispatched?.payload,
    );
    if (response?.status === true) {
      yield put(actions.setClusterSWOTAnalysis({ companyId: dispatched?.payload?.companyId, results: response?.response }));
    } else {
      yield put(actions.setClusterSWOTAnalysisFailed());
    }
  } catch (err) {
    console.log(err);
  }

}

export function* getClusterCompanySecFillings(dispatched: PayloadAction<GetProfileRequest>) {
  try {
    const response: CompanyFilingResponse = yield call(
      axios.post,
      API.COMPANY_SEC_FILINGS,
      dispatched?.payload,
    );
    if (response?.status === true) {
      yield put(actions.setClusterCompanySecFillings({ companyId: dispatched?.payload?.companyId, results: response?.response }));
    } else {
      yield put(actions.setClusterCompanySecFillings({ companyId: dispatched?.payload?.companyId, results: null }));
    }
  } catch (err) {
    console.log(err);
    yield put(actions.setClusterCompanySecFillings({ companyId: dispatched?.payload?.companyId, results: null }));
  }
}

export function* getClusterCompanyNews(dispatched: PayloadAction<GetClusterCompanyNewsRequest>) {
  try {
    const response: CompanyNewsResponse = yield call(
      axios.post,
      API.COMPANY_NEWS,
      dispatched?.payload,
    );
    if (response?.status === true) {
      yield put(actions.setClusterCompanyNews(response?.response?.companyNews));
      yield put(actions.setClusterNewsFilters(response?.response?.filters));
    }
  } catch (err) {
    console.log(err);
  }
}

export function* refreshClusterSWOTAnalysis(dispatched: PayloadAction<CompanySWOTRefresh>) {
  try {
    const response: SWOTRefreshResponse = yield call(
      axios.post,
      API.REFRESH_SWOT_ANALYSIS,
      dispatched?.payload,
    );
    const { companyId } = dispatched.payload;
    yield put(actions.removeRefreshClusterCompanyId(companyId));
    if (response?.status === true) {
      const { status, message, flag } = response.response;
      yield put(actions.setRefreshClusterSWOTAnalysis({ companyId: dispatched.payload.companyId, data: response?.response }));
      if (status) {
        yield put(
          helperActions.setNotification({
            message: message as string,
            proTip: false,
            rightPlacement: false,
            groupId: 0,
            classnameFlag: true,
          }),
        );
        yield put(actions.setClusterSwotRefreshedCompany(companyId));
      } else if (flag === 'updated') {
        yield put(actions.getClusterSWOTAnalysis({ companyId }));
      } else {
        yield put(
          helperActions.setNotification({
            message: message || 'SWOT Analysis refresh failed. Please try again',
            proTip: false,
            rightPlacement: false,
            groupId: 0,
            classnameFlag: false,
          }),
        );
      }
    }

  } catch (err) {
    console.log(err);
    yield put(actions.removeRefreshClusterCompanyId(dispatched?.payload?.companyId));
    yield put(actions.setRefreshClusterSWOTLoading({ companyId: dispatched.payload.companyId, flag: false }));
    yield put(
      helperActions.setNotification({
        message: 'SWOT Analysis refresh failed. Please try again',
        proTip: false,
        rightPlacement: false,
        groupId: 0,
        classnameFlag: false,
      }),
    );
  }
}

export function* getClusterProductAndServices(dispatched: PayloadAction<GetProductAndServicesRequest>) {
  try {
    const { payload } = dispatched;
    const response: ProductAndServiceResponse = yield call(
      axios.post,
      API.GET_PRRODUCT_AND_SERVICES,
      payload,
    );
    if (response?.status === true) {
      const data = {
        products: response?.response?.products,
        services: response?.response?.services,
      };
      yield put(actions.setClusterProductAndServices({ companyId: payload.companyId, results: data }));
    }
  } catch (err) {
    console.log(err);
  }

}

export function* clustersSaga() {
  yield takeLatest(actions.getAccountClusters.type, getAccountClusters);
  yield takeLatest(actions.manageAccountCluster.type, manageAccountCluster);
  yield takeLatest(actions.shareAccountClusters.type, shareAccountClusters);
  yield takeLeading(actions.getClusterDetails.type, getClusterDetails);
  yield takeEvery(actions.getClusterCompanyProfile.type, getClusterCompanyProfile);
  yield takeEvery(actions.getClusterStocksChart.type, getClusterStocksChart);
  yield takeEvery(actions.getClusterStrategicHighlight.type, getClusterStrategicHighlight);
  yield takeEvery(actions.getClusterSWOTAnalysis.type, getClusterSWOTAnalysis);
  yield takeEvery(actions.getClusterCompanySecFillings.type, getClusterCompanySecFillings);
  yield takeEvery(actions.getClusterCompanyNews.type, getClusterCompanyNews);
  yield takeEvery(actions.refreshClusterSWOTAnalysis.type, refreshClusterSWOTAnalysis);
  yield takeEvery(actions.getClusterProductAndServices.type, getClusterProductAndServices);
}